import { useAuth as useAuthContext } from '@/contexts/AuthContext'

/**
 * Custom hook to access authentication state and methods
 * 
 * @returns Auth context with user, session, loading state and auth methods
 * @throws Error if used outside of AuthProvider
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { user, loading, signIn, signOut } = useAuth()
 *   
 *   if (loading) return <div>Loading...</div>
 *   if (!user) return <div>Please log in</div>
 *   
 *   return (
 *     <div>
 *       <p>Welcome, {user.email}!</p>
 *       <button onClick={signOut}>Sign Out</button>
 *     </div>
 *   )
 * }
 * ```
 */
export const useAuth = useAuthContext

/**
 * Hook to check if user is authenticated
 * 
 * @returns boolean indicating if user is authenticated
 */
export function useIsAuthenticated(): boolean {
  const { user, loading } = useAuth()
  return !loading && !!user
}

/**
 * Hook to get current user's JWT token
 * 
 * @returns JWT token string or null if not authenticated
 */
export function useJWTToken(): string | null {
  const { session } = useAuth()
  return session?.access_token ?? null
}
